import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { adminAuthApi, aiApi, cseMarketApi } from '../services/api.js';

// ── Design-system class constants ───────────────────────────────────────────
// Mirrors AdminCseMarketPage.jsx / AdminPaymentsPage.jsx.

const cardClass = 'rounded-xl border border-slate-200 bg-white shadow-sm';
const btnPrimary = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondary = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50';
const textareaClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-y min-h-[120px]';
const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';

const IconBack = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const IconUpload = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M10 12.5a.75.75 0 00.75-.75V4.56l1.97 1.97a.75.75 0 101.06-1.06l-3.25-3.25a.75.75 0 00-1.06 0L6.22 5.47a.75.75 0 101.06 1.06l1.97-1.97v7.19c0 .414.336.75.75.75z" />
    <path d="M3.5 12.75a.75.75 0 01.75.75v2.5c0 .414.336.75.75.75h10a.75.75 0 00.75-.75v-2.5a.75.75 0 011.5 0v2.5A2.25 2.25 0 0115 18.5H5a2.25 2.25 0 01-2.25-2.25v-2.5a.75.75 0 01.75-.75z" />
  </svg>
);

const IconFile = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0 text-slate-400">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);

const IconClear = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M10 8.586L6.707 5.293a1 1 0 10-1.414 1.414L8.586 10l-3.293 3.293a1 1 0 101.414 1.414L10 11.414l3.293 3.293a1 1 0 001.414-1.414L11.414 10l3.293-3.293a1 1 0 00-1.414-1.414L10 8.586z" clipRule="evenodd" />
  </svg>
);

const ACCEPTED_EXTENSIONS = '.pdf,.xls,.xlsx,.csv,.txt,image/*';
const MAX_FILES = 6;

const TABLE_OPTIONS = [
  { id: 'trade-summary', label: 'Trade Summary' },
  { id: 'detailed-trades', label: 'Detailed Trades' },
  { id: 'market-cap', label: 'Market Capitalization' },
  { id: 'gics-summary', label: 'GICS Industry Summary' },
];

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(1)} ${units[i]}`;
};

// Renders Gemini's markdown response with Tailwind Typography, scoped so it
// never bleeds into the rest of the admin panel's styling.
const markdownComponents = {
  table: (props) => <div className="overflow-x-auto"><table {...props} /></div>,
};

const AdminAiAnalysisPage = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [ticker, setTicker] = useState('');
  const [symbols, setSymbols] = useState([]);
  // A single day is just a range where both ends match — same convention as
  // AdminCseMarketPage's date picker. Both blank means "latest date".
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [resultFileNames, setResultFileNames] = useState([]);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAuthApi.getCurrentUser();
        if (res.data?.authenticated) setIsAuthenticated(true);
      } catch {}
      finally { setAuthChecked(true); }
    })();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [symbolsRes, datesRes] = await Promise.all([
          cseMarketApi.getSymbols(),
          cseMarketApi.getAvailableDates(),
        ]);
        setSymbols(symbolsRes.data?.symbols || []);
        setAvailableDates(datesRes.data?.dates || []);
      } catch {}
    })();
  }, [isAuthenticated]);

  const oldestAvailable = availableDates[availableDates.length - 1] || '';
  const latestAvailable = availableDates[0] || '';

  const toggleTable = (id) => {
    setSelectedTables((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, MAX_FILES));
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() && files.length === 0 && selectedTables.length === 0) {
      setError('Add a prompt, a file, or select at least one database table.');
      return;
    }
    setSubmitting(true);
    setError('');
    setResult('');
    setAttachments([]);
    setResultFileNames([]);
    try {
      const formData = new FormData();
      if (prompt.trim()) formData.append('prompt', prompt.trim());
      files.forEach((f) => formData.append('files', f));
      if (selectedTables.length) {
        formData.append('tablesJson', JSON.stringify(selectedTables));
        formData.append('ticker', ticker.trim() || 'ALL');
        if (dateFrom || dateTo) {
          formData.append('startDate', dateFrom || dateTo);
          formData.append('endDate', dateTo || dateFrom);
        }
      }
      const res = await aiApi.analyze(formData);
      setResult(res.data?.result || '');
      setAttachments(res.data?.attachments || []);
      setResultFileNames(res.data?.fileNames || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setPrompt('');
    setFiles([]);
    setSelectedTables([]);
    setTicker('');
    setDateFrom('');
    setDateTo('');
    setResult('');
    setAttachments([]);
    setResultFileNames([]);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <svg className="h-4 w-4 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Checking session…
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-lg font-bold text-white shadow-sm mb-3 mx-auto">J</div>
          <p className="text-xl font-semibold tracking-tight text-slate-900 mb-1">AI Document Analysis</p>
          <p className="text-slate-500 text-sm mb-6">Sign in to the admin dashboard to view this page.</p>
          <Link to="/admin/payments" className={`inline-flex ${btnPrimary}`}>Go to Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">J</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">AI Document Analysis</p>
            <p className="text-[11px] text-slate-400 uppercase tracking-widest">Admin · Gemini</p>
          </div>
          <Link to="/admin/cse-market" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-indigo-600">
            <IconBack /> CSE Market Data
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Ask a question about a document</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload files, pull in CSE database data, and/or type a prompt — Gemini reads whatever you attach and answers.
          </p>
        </div>

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className={`${cardClass} p-5 space-y-5`}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Summarize the key figures in this file, or ask any question…"
              className={textareaClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Files (optional, up to {MAX_FILES}, 15MB each)
            </label>
            <div className="space-y-2">
              {files.map((f, idx) => (
                <div key={`${f.name}-${idx}`} className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
                  <IconFile />
                  <span className="flex-1 min-w-0 truncate text-slate-700">{f.name}</span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{formatBytes(f.size)}</span>
                  <button type="button" onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-600">
                    <IconClear />
                  </button>
                </div>
              ))}
              {files.length < MAX_FILES && (
                <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 cursor-pointer hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                  <IconUpload /> {files.length ? 'Add another file' : 'Click to choose PDF(s), XLS/XLSX, CSV, TXT, or image(s)'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_EXTENSIONS}
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">CSE database data (optional)</label>
            <p className="text-xs text-slate-500 mb-2">Only the tables you select — and only the matching ticker's rows — are sent to Gemini.</p>
            <div className="flex flex-wrap gap-3 mb-3">
              {TABLE_OPTIONS.map((t) => (
                <label key={t.id} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTables.includes(t.id)}
                    onChange={() => toggleTable(t.id)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {t.label}
                </label>
              ))}
            </div>
            {selectedTables.length > 0 && (
              <div className="flex flex-wrap items-end gap-4">
                <div className="max-w-xs">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ticker (leave blank or "ALL" for every company)</label>
                  <input
                    type="text"
                    list="cse-symbols"
                    value={ticker}
                    onChange={(e) => setTicker(e.target.value.toUpperCase())}
                    placeholder="ALL"
                    className={inputClass}
                  />
                  <datalist id="cse-symbols">
                    <option value="ALL" />
                    {symbols.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    min={oldestAvailable || undefined}
                    max={dateTo || latestAvailable || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
                  <input
                    type="date"
                    value={dateTo}
                    min={dateFrom || oldestAvailable || undefined}
                    max={latestAvailable || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={inputClass}
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }} className={`${btnSecondary} !px-3 !py-2`}>
                    Clear (use all dates)
                  </button>
                )}
              </div>
            )}
            {selectedTables.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                Will use: <span className="font-medium text-slate-700">
                  {dateFrom || dateTo
                    ? (dateFrom === dateTo || !dateTo ? (dateFrom || dateTo) : `${dateFrom || 'earliest'} to ${dateTo}`)
                    : 'all available dates'}
                </span>{!dateFrom && !dateTo && ' — pick a From/To range to narrow it to specific days instead.'}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={submitting} className={btnPrimary}>
              {submitting ? 'Analyzing…' : 'Analyze'}
            </button>
            {(prompt || files.length > 0 || selectedTables.length > 0 || result) && (
              <button type="button" className={btnSecondary} disabled={submitting} onClick={resetAll}>
                Reset
              </button>
            )}
          </div>
        </form>

        {!submitting && (attachments.length > 0 || resultFileNames.length > 0) && (
          <div className={`${cardClass} p-4 mt-6 text-xs`}>
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Data used for this analysis</h2>
            <ul className="space-y-1">
              {attachments.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-x-2 text-slate-600">
                  <span className="font-medium text-slate-800">{a.label}</span>
                  <span>· {a.range}</span>
                  <span>· ticker {a.ticker}</span>
                  <span>· {a.rowCount.toLocaleString()}{a.truncated ? ` of ${a.matchedCount.toLocaleString()}` : ''} rows</span>
                  {a.truncated && (
                    <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 font-medium">truncated</span>
                  )}
                </li>
              ))}
              {resultFileNames.map((name) => (
                <li key={name} className="flex items-center gap-1.5 text-slate-600">
                  <IconFile /> {name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(submitting || result) && (
          <div className={`${cardClass} p-5 mt-6`}>
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Result</h2>
            {submitting ? (
              <div className="flex items-center gap-3 text-sm text-slate-500 py-6">
                <svg className="h-4 w-4 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Gemini is reading the document…
              </div>
            ) : (
              <div className="prose prose-sm prose-slate max-w-none prose-headings:font-semibold prose-a:text-indigo-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {result}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAiAnalysisPage;
